import React from 'react';
import moment from 'moment';

// Services & Helpers
import API from 'API';
import FormHelper from 'helpers/FormHelper';
import TextHelpers from 'helpers/TextHelpers';
import ReferenceHelpers from 'helpers/ReferenceHelpers';
import DataHelpers from 'helpers/DataHelpers';
import Print from 'Print';

// Components
import Loader from 'components/common/Loader';
import SearchBox from 'components/common/SearchBox';
import PriceCalcModal from 'components/appt/PriceCalcModal';
import PreviewEmailModal from 'components/outgoing/PreviewEmailModal';
import CustomerCommsModal from 'components/customer/CustomerCommunication/CustomerCommsModal';
import CurrencyInput from 'components/common/CurrencyInput';
import EditCustomerModal from 'components/customer/EditCustomerModal';
import EditApptResultsModal from 'components/appt/EditApptResultsModal';
import EditInvoiceModal from 'components/invoice/EditInvoiceModal';

//-----------------------------------------------------------------

class EditAppt extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            appt: null,
            appts: [],
            properties: [],
            technicians: [],
            nonBookingApptTypes: [],
            suggestedAppts: null,
            discounts: []
        };

        this.changedFields = {};
        this.priceCalcModalRef = React.createRef();
        this.previewEmailModalRef = React.createRef();
        this.customerCommsModalRef = React.createRef();
        this.editCustomerModalRef = React.createRef();
        this.editApptResultsModalRef = React.createRef();
        this.editInvoiceModalRef = React.createRef();

        this.form = new FormHelper({
            fields: {
                technicianUserID: {
                    label: 'Technician',
                    type: 'single-select',
                    isReadOnly: () => this.state.isLocked,
                    getOptions: () => this.state.technicians.map(t => ({
                        value: t.id,
                        text: t.nickname
                    }))
                },
                propertyID: {
                    label: 'Property',
                    type: 'single-select',
                    isReadOnly: () => this.state.isLocked,
                    getOptions: () => this.state.properties.map(p => ({
                        value: p.id,
                        text: p.name
                    })),
                    getLabelExtras: () => (this.state.appt.property &&
                        <div className="appt-editor-price-scheme ms-auto">
                            <span className="fa-solid fa-coins" />{' '}
                            Price Scheme: {this.state.appt.property.priceSchemeName || '(None)'}
                        </div>
                    ),
                    getExtras: () => this.state.appt.property && !!this.state.appt.property.notes && (
                        <div className="appt-editor-important-notes mb-3">
                            <span className="fa-solid fa-comment" />{' '}
                            {this.state.appt.property.notes}
                        </div>
                    )
                },
                date: {
                    label: 'Date',
                    type: 'date',
                    isReadOnly: () => this.state.isLocked
                },
                time: {
                    label: 'Time',
                    type: 'single-select',
                    blankText: '(Select)',
                    getOptions: () => ReferenceHelpers.listTimes().map(t => ({
                        value: t,
                        text: `${t}`
                    })),
                    isReadOnly: () => this.state.isLocked
                },
                duration: {
                    label: 'Duration',
                    //type: 'number',
                    type: 'single-select',
                    getOptions: () => ReferenceHelpers.Durations.map(d => ({
                        value: d,
                        text: `${d}`
                    })),
                    isReadOnly: () => this.state.isLocked//,
                    //props: {
                    //    step: 5,
                    //    min: 0
                    //},
                },
                discountID: {
                    label: 'Discount',
                    type: 'single-select',
                    getOptions: () => this.state.discounts.map(d => ({
                        value: d.id,
                        text: this.getDiscountName(d)
                    })),
                    blankText: 'None'
                },
                servicePrice: {
                    label: 'Service Price',
                    type: 'currency',
                    getLabelExtras: () => (
                        <button type="button" className="btn btn-sm btn-primary ms-auto" title="Quotation" onClick={() => this.showPriceCalcModal()}>
                            <span className="fa fa-clipboard"></span>
                        </button>
                    )
                },
                productPrice: {
                    label: 'Product Price',
                    type: 'currency',
                    isReadOnly: true
                },
                cancellationFee: {
                    label: 'Cancellation Fee',
                    type: 'currency'
                },
                price: {
                    label: 'Total price',
                    type: 'currency',
                    isReadOnly: true
                },
                notes: {
                    label: 'Notes',
                    type: 'expanding-text',
                    rows: 2
                },
                skipBookingNotification: {
                    boxLabel: 'Skip booking notification',
                    type: 'checkbox',
                },
                isRecurring: {
                    boxLabel: 'Recurring appointment',
                    type: 'checkbox',
                    isReadOnly: () => !!this.state.appt.id
                }
            },
            getValue: (fieldName) => this.state.appt[fieldName],
            setValue: (fieldName, value) => {
                this.updateFields({ [fieldName]: value });
                this.isChanged = true;
            }
        });

        this.recurringApptForm = new FormHelper({
            fields: {
                startDate: {
                    label: 'Start date',
                    type: 'date',
                    isReadOnly: () => (this.state.appt && this.state.appt.recurringAppt && this.state.appt.recurringAppt.id)
                },
                endDate: {
                    label: 'End date',
                    type: 'date'
                },
                frequencyType: {
                    label: 'Frequency type',
                    type: 'single-select',
                    getOptions: () => [
                        { value: 'Days', text: 'Days' },
                        { value: 'Weeks', text: 'Weeks' },
                        { value: 'Months', text: 'Months' },
                        { value: 'Years', text: 'Years' }
                    ]
                },
                frequency: {
                    label: 'Frequency',
                    type: 'number'
                }
            },
            getValue: (fieldName) => this.state.appt.recurringAppt[fieldName],
            setValue: (fieldName, value) => this.updateRecurringApptFields({ [fieldName]: value })
        });
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(oldProps) {
        if (this.props.id != oldProps.id) {
            this.load();
        }
        const fields = {};
        let fieldsChanged = false;
        if (this.props.date != oldProps.date) {
            fields.date = this.props.date;
            fieldsChanged = true;
        }
        if (this.props.time != oldProps.time) {
            fields.time = this.props.time;
            fieldsChanged = true;
        }
        if (this.props.technicianUserID != oldProps.technicianUserID) {
            fields.technicianUserID = this.props.technicianUserID;
            fieldsChanged = true;
        }
        if (this.props.duration != oldProps.duration) {
            fields.duration = this.props.duration;
            fieldsChanged = true;
        }
        if (fieldsChanged) {
            this.updateFields(fields);
            this.isChanged = true;
        }
    }

    async load(silent) {
        return new Promise(async (resolve, reject) => {
            const { id, date, time, customerID, propertyID, rebookID, duration } = this.props;
            let { technicianUserID } = this.props;
            let appt;

            // Load related entities
            const techniciansReq = API.call('user/list', { isTechnician: true });
            const serviceTypesReq = API.call('service-type/list', { getPricing: true });
            const technicians = await techniciansReq;
            const serviceTypes = await serviceTypesReq;
            const serviceTypesByCode = {};
            const serviceTypesByID = {};
            serviceTypes.forEach(st => {
                serviceTypesByCode[st.code] = st;
                serviceTypesByID[st.id] = st;
            });

            // Load appt
            if (id) {
                if (!silent) {
                    this.setState({ isLoading: true });
                }
                appt = await API.call(`appt/get/${id}`);
                appt.isRecurring = !!appt.recurringAppt;
                this.updateTotal(appt);
            } else {
                if (!technicianUserID && technicians.length > 0) {
                    technicianUserID = technicians[0].id;
                }
                appt = {
                    date, time, technicianUserID, customerID, propertyID, rebookID, duration,
                    technician: (technicianUserID ? technicians.find(t => t.id == technicianUserID) : null),
                    status: 'Open',
                    services: [],
                    notifications: []
                };
                this.isChanged = true;
            }

            // Update UI
            this.setState({
                isLoading: false,
                appt,
                isLocked: (!!appt.id && appt.status != 'Open'),
                technicians,
                serviceTypes,
                serviceTypesByCode,
                serviceTypesByID
            }, async () => {
                await this.loadCustomer();
                this.loadDiscounts();
                if (appt.customerID) {
                    await this.loadProperties();
                    //await this.loadRebooks();
                }
                if (this.props.onApptUpdate) {
                    this.props.onApptUpdate(appt);
                }
                if (this.props.onUpdateControlPanel) {
                    this.props.onUpdateControlPanel(this.renderControlPanel());
                }

                // Load non-booking appt types silently
                const nonBookingApptTypes = await API.call('non-booking-appt-type/list');
                this.setState({
                    nonBookingApptTypes
                }, resolve);
            });
        });
    }

    loadServicePrice(serviceTypeIDs) {
        const { appt } = this.state;
        if (appt.status != 'Open') {
            return;
        }
        return new Promise((resolve) => {
            clearTimeout(this.loadPriceTimeout);
            this.loadPriceTimeout = setTimeout(async () => {
                const apptPricing = await API.call('appt/get-price', this.state.appt);
                const services = [...this.state.appt.services];
                const discount = this.state.discounts.find(d => d.id == appt.discountID);

                let discountAmount = 0;
                let servicePrice = 0;
                services.forEach(ast => {
                    if (!serviceTypeIDs || serviceTypeIDs[ast.serviceTypeID]) {
                        const newService = apptPricing.services.find(ast2 =>
                            ast2.serviceTypeID == ast.serviceTypeID &&
                            ast2.cookerID == ast.cookerID
                        );
                        if (newService && newService.price > 0) {
                            ast.price = newService.price;
                        }
                    }
                    const thisServiceDiscountAmount = this.getDiscountAmount(ast, discount);
                    servicePrice += (ast.price || 0) - thisServiceDiscountAmount;
                    discountAmount += thisServiceDiscountAmount;
                });

                this.updateFields({
                    servicePrice,
                    discountAmount,
                    services
                }, resolve, true);
                if (servicePrice != this.state.appt.servicePrice) {
                    this.isChanged = true;
                }
            }, 10);
        });
    }
    
    getDiscountAmount(apptServiceType, discount) {
        const { serviceTypes } = this.state;
        const price = (Number(apptServiceType.price) || 0);
        
        let discountAmount = 0;
        if (discount) {
            if (discount.discountType == 'Percentage') {
                const serviceType = serviceTypes.find(st => st.id == apptServiceType.serviceTypeID);

                if (serviceType && (
                    (serviceType.code == 'clean' && discount.appliesToClean) ||
                    (['clean'].indexOf(serviceType.code) == -1 && discount.appliesToOtherService)
                )) {
                    discountAmount = price * (discount.discountAmount || 0) / 100;
                    discountAmount = Math.round(discountAmount * 100) / 100;
                }
            }
        }

        if (discountAmount > price) {
            discountAmount = price;
        }

        return discountAmount;
    }
    
    loadDuration() {
        clearTimeout(this.loadDurationTimeout);
        this.loadDurationTimeout = setTimeout(async () => {
            const duration = await API.call('appt/get-duration', this.state.appt);
            if (duration != this.state.appt.duration) {
                this.updateFields({ duration });
                this.isChanged = true;
            }
        }, 100);
    }

    loadDiscounts() {
        const apptDate = moment(this.state.appt.date).format('YYYY-MM-DD');
        if (apptDate == this.lastDateLoadedDiscounts) {
            return;
        }
        this.lastDateLoadedDiscounts = apptDate;
        clearTimeout(this.loadDiscountsTimeout);
        this.loadDiscountsTimeout = setTimeout(async () => {
            const { discountID } = this.state.appt;
            const discounts = await API.call('discount/list', {
                date: apptDate,
                forceDiscountID: discountID
            });
            this.setState({ discounts });
            if (discountID && !discounts.find(d => d.id == discountID)) {
                this.updateFields({ discountID: null });
                this.isChanged = true;
            }
        }, 100);
    }

    async save(appt) {
        return new Promise(async (resolve) => {
            if (!appt) {
                appt = this.state.appt;
            }
            if (!this.state.isChanged && appt.id) {
                return resolve(appt.id);
            }
            
            const { onSave } = this.props;
            this.setState({ isLoading: true });
            this.changedFields = {};
            try {
                if (this.isChanged) {
                    const { id, sendApptBookedNotification, sendCancellationInvoice, invoiceID } = await API.call('appt/save', appt);
                    this.updateFields({
                        id,
                        invoiceID
                    }, async () => {
                        this.setState({
                            isLoading: false,
                            isChanged: !appt.isDraft
                        });
                        if (onSave && !appt.isDraft) {
                            onSave(appt);
                        }
                        resolve({ id, sendApptBookedNotification, sendCancellationInvoice, invoiceID });
                    });
                } else {
                    if (onSave && !appt.isDraft) {
                        onSave(appt);
                    }
                    resolve({ id: appt.id, sendApptBookedNotification: false, sendCancellationInvoice: false });
                }
            } catch (error) {
                this.setState({
                    isLoading: false
                }, () => {
                    alert(error); 
                });
            }
        });
    }

    async complete() {
        const confirm = window.confirm('Complete this appointment?');
        if (!confirm) return;
        await API.call('appt/complete/' + this.state.appt.id);
        this.props.onClose(true);
    }

    async saveAndClose() {
        const { appt } = this.state;
        const hasSentBookingNotification = appt.notifications.some(n => n.type == 'ApptBooked');
        
        // Non-booking appt type - just save once
        if (appt.nonBookingApptTypeID || appt.isRecurring) {
            await this.save();
            this.props.onClose(true);
            return;
        }

        const hasUncheckedService = appt.property.cookers.some(cooker => {
            const cleanService = this.findService(cooker.id, 'clean');
            if (cleanService && !cleanService.price) return true;
            return false;
        });

        if (appt.status === 'Open' && hasUncheckedService) {
            alert('All selected cooker services must have a price. Please set all prices and try saving again.');
            return;
        }

        // Check for price
        if (appt.status === 'Open' && !appt.servicePrice) {
            const confirm = window.confirm('The appointment has no price (which may be because there is no price scheme set). Are you sure you want to continue?');
            if (!confirm) return;
        }

        // If we've already sent a notification, do a full save
        if (hasSentBookingNotification) {
            const { sendApptBookedNotification, sendCancellationInvoice } = await this.save();

            // If we have rescheduled the appointment, offer to re-send the booking notification
            if (sendApptBookedNotification) {
                await this.confirmSendApptBookedNotification(appt.id);
            }

            // If the appt is cancelled and there is a cancellation fee, offer to email the invoice
            if (sendCancellationInvoice) {
                await this.emailInvoice();
            }

            this.props.onClose(true);
            return;
        }
        
        // Otherwise, draft save to determine whether we should send a booking notification
        appt.isDraft = true;
        const { sendApptBookedNotification } = await this.save(appt);

        // Then save fully
        this.updateFields({
            isDraft: false
        }, async () => {
            const { id } = await this.save();
                
            if (sendApptBookedNotification && !appt.skipBookingNotification) {
                const send = await this.confirmSendApptBookedNotification(id);

                // If we don't send, tick 'skip booking notification' automatically
                if (!send) {
                    this.updateFields({
                        skipBookingNotification: true
                    }, () => {
                        this.save();
                    });
                }
            }
            this.props.onClose(true);
        });
    }

    async emailInvoice() {
        const { appt } = this.state;
        const email = {
            type: 'Invoice',
            apptID: appt.id
        };
        const send = await this.previewEmailModalRef.current.open({
            canSend: true,
            email
        });
        if (send) {
            await API.call('email/send', email);
            window.alert('The invoice has been sent.');
        }
    }

    async emailReceipt() {
        const { appt } = this.state;
        const email = {
            type: 'Receipt',
            apptID: appt.id
        };
        const send = await this.previewEmailModalRef.current.open({
            canSend: true,
            email
        });
        if (send) {
            await API.call('email/send', email);
            window.alert('The receipt has been sent.');
        }
    }

    updateFields(fields, callback, dontLoadPrice) {
        const appt = { ...this.state.appt };
        let anyChanged = false, loadCustomer = false, loadProperty = false, loadPrice = false, loadDuration = false, loadDiscounts = false;

        for (let fieldName in fields) {
            const oldValue = appt[fieldName];
            const value = fields[fieldName];
            if (value != oldValue) {
                anyChanged = true;
                appt[fieldName] = value;
                this.changedFields[fieldName] = true;

                switch (fieldName) {
                    case 'technicianUserID':
                        appt.technician = this.state.technicians.find(s => s.id == appt.technicianUserID);
                        break;
                    case 'customerID':
                        loadCustomer = true;
                        loadPrice = true;
                        appt.services = [];
                        appt.autoPlannerResultID = null;
                        appt.autoPlannerResultOptionID = null;
                        break;
                    case 'propertyID':
                        appt.property = this.state.properties.find(p => p.id == appt.propertyID);
                        loadPrice = true;
                        loadProperty = true;
                        loadDiscounts = true;
                        appt.autoPlannerResultID = null;
                        appt.autoPlannerResultOptionID = null;
                        break;
                    case 'services':
                    case 'discountID':
                        if (appt.status == 'Open') {
                            loadDuration = !dontLoadPrice;
                            loadPrice = !dontLoadPrice;
                        }
                        const discount = this.state.discounts.find(d => d.id == appt.discountID);
                        appt.servicePrice = 0;
                        appt.discountAmount = 0;
                        appt.services.forEach(ast => {
                            const discountAmount = this.getDiscountAmount(ast, discount);
                            appt.servicePrice += (ast.price || 0) - discountAmount;
                            appt.discountAmount += discountAmount;
                        });
                        if (discount && discount.discountType == 'Fixed') {
                            appt.servicePrice -= (Number(discount.discountAmount) || 0);
                        }
                        this.updateTotal(appt);
                        break;
                    case 'date':
                        if (this.props.onSetDate) {
                            this.props.onSetDate(value);
                        }
                        appt.autoPlannerResultID = null;
                        appt.autoPlannerResultOptionID = null;
                        loadDiscounts = true;
                        break;
                    case 'servicePrice':
                    case 'productPrice':
                        this.updateTotal(appt);
                        break;
                    case 'isRecurring':
                        if (value) {
                            if (!appt.recurringAppt) {
                                appt.recurringAppt = {
                                    startDate: moment(appt.date).format('YYYY-MM-DD'),
                                    frequencyType: 'Days',
                                    frequency: 1
                                };
                            }
                        } else {
                            appt.recurringAppt = null;
                        }
                        break;
                }
            }
        }

        if (anyChanged) {
            this.setState({
                appt,
                isLoadingProperties: loadCustomer,
                isChanged: true,
                suggestedAppts: (loadCustomer ? null : this.state.suggestedAppts)
            }, async () => {
                if (loadCustomer) {
                    await this.loadCustomer();
                    await this.loadProperties();
                }
                if (loadProperty) {
                    this.loadProperty(false);
                }
                if (loadPrice) {
                    this.loadServicePrice(null);
                }
                if (loadDuration) {
                    this.loadDuration();
                }
                if (loadDiscounts) {
                    this.loadDiscounts();
                }
                if (callback) callback();
                if (this.props.onApptUpdate) {
                    this.props.onApptUpdate(appt);
                }
                if (this.props.onUpdateControlPanel) {
                    this.props.onUpdateControlPanel(this.renderControlPanel());
                }
            });
        } else if (callback) {
            callback();
        }
    }

    updateRecurringApptFields(fields) {
        const recurringAppt = { ...this.state.appt.recurringAppt };
        for (let fieldName in fields) {
            const value = fields[fieldName];
            recurringAppt[fieldName] = value;
        }
        this.updateFields({ recurringAppt });
        this.isChanged = true;
    }

    loadCustomer() {
        return new Promise(async (resolve) => {
            const { customerID } = this.state.appt;
            const fields = {};
            if (customerID) {
                this.setState({ isLoadingCustomer: true });
                fields.customer = await API.call('customer/get/' + customerID);
            } else {
                fields.customer = null;
                fields.property = null;
            }
            this.updateFields(fields, () => {
                this.setState({
                    isLoadingCustomer: false
                }, resolve);
            });
        });
    }
    
    //loadRebooks() {
    //    return new Promise(async (resolve) => {
    //        const { customerID, propertyID } = this.state.appt;
    //        const rebooks = await API.call(`rebook/get-for-customer`, {
    //            customerID,
    //            propertyID
    //        });
    //        this.setState({ rebooks });
    //    });
    //}

    loadProperties() {
        return new Promise(async (resolve) => {
            const { customerID, propertyID, property } = this.state.appt;

            if (customerID) {
                this.setState({ isLoadingProperties: true });
                const properties = await API.call('property/list', { customerID });

                this.updateFields({
                    propertyID: propertyID || (properties.length > 0 ? properties[0].id : null),
                    property: property || (properties.length > 0 ? properties[0] : null)
                }, () => {
                    this.setState({
                        properties,
                        isLoadingProperties: false
                    });
                    this.loadProperty(false);
                    resolve();
                });
            } else {
                this.updateFields({
                    propertyID: null,
                    property: null
                }, () => {
                    this.setState({ properties: [] });
                    this.loadProperty(false);
                    resolve();
                });
            }
        });
    }

    async loadProperty(force) {
        const { appt } = this.state;
        if (appt.property && appt.property.cookers && !force) {
            const oldPropertyID = (appt.property || { id: 0 }).id;
            if (appt.propertyID == oldPropertyID) {
                return;
            }
        }
        let property;
        if (appt.propertyID) {
            this.setState({ isLoadingProperty: true });
            property = await API.call('property/get/' + appt.propertyID);
        }
        this.updateFields({
            property
        }, async () => {
            // Pre-tick services
            if (appt.services.length == 0 && property && property.cookers && appt.status == 'Open') {
                const services = [];
                property.cookers.forEach(c => {
                    if (c.isInUse) {
                        //services.push({
                        //    cookerID: c.id,
                        //    serviceTypeID: .id
                        //});
                    }
                });
                this.updateFields({ services }, () => {
                    this.loadServicePrice(null);
                });
            }
            this.setState({ isLoadingProperty: false });

            // If new appt, check for existing appt at this property
            if (!appt.id && appt.propertyID) {
                const { id, date } = await API.call('appt/get-open-appt/' + appt.propertyID); 
                this.setState({
                    openApptID: id,
                    openApptDate: (date ? moment(date).format('YYYY-MM-DD') : null)
                });
            }
        });
    }

    async confirmCancel() {
        const { appt } = this.state;
        const confirm = window.confirm('Are you sure you want to cancel this appointment?');
        if (confirm) {
            this.setState({ isLoading: true });
            await API.call('appt/cancel/' + appt.id);
            this.props.onClose(true);
        }
    }

    async confirmCancelRecurring() {
        const { appt } = this.state;
        const confirm = window.confirm('Delete this recurring appointment (this will delete all future occurrences)?');
        if (confirm) {
            this.setState({ isLoading: true });
            await API.call('appt/delete-recurring/' + appt.recurringAppt.id);
            this.props.onClose(true);
        }
    }

    findServiceIndex(serviceTypeCodeOrID, cookerID) {
        const { appt, serviceTypesByCode } = this.state;
        if (Number(serviceTypeCodeOrID)) {
            const index = appt.services.findIndex(s => s.serviceTypeID == serviceTypeCodeOrID && s.cookerID == cookerID);
            return index;
        } else {
            const serviceType = serviceTypesByCode[serviceTypeCodeOrID];
            if (serviceType) {
                const index = appt.services.findIndex(s => s.serviceTypeID == serviceType.id && s.cookerID == cookerID);
                return index;
            }
        }
        return -1;
    }

    findService(cookerID, serviceTypeCode) {
        const { appt } = this.state;
        const index = this.findServiceIndex(serviceTypeCode, cookerID);
        return (index != -1 ? appt.services[index] : null);
    };

    toggleService(serviceTypeCodeOrID, cookerID) {
        const { appt, serviceTypes, serviceTypesByCode } = this.state;
        let serviceType;
        if (Number(serviceTypeCodeOrID)) {
            serviceType = serviceTypes.find(st => st.id == serviceTypeCodeOrID);
        } else {
            serviceType = serviceTypesByCode[serviceTypeCodeOrID];
        }
        const services = [...appt.services];
        const index = this.findServiceIndex(serviceTypeCodeOrID, cookerID);

        if (index == -1) {
            const service = {
                cookerID,
                serviceTypeID: serviceType.id
            };
            services.push(service);
        } else {
            services.splice(index, 1);
        }
        this.updateFields({
            services
        }, () => {
            this.loadServicePrice({ [serviceType.id]: true });
        });
    }

    updateService(cookerID, otherServiceIndex, fields, loadPrice) {
        const { appt } = this.state;
        let services = [...appt.services];
        let index = (cookerID ? services.findIndex(ast => ast.cookerID == cookerID) : otherServiceIndex);
        if (index == -1) {
            services.push({ cookerID });
            index = services.length - 1;
        }
        const service = services[index];
        for (let fieldName in fields) {
            const value = fields[fieldName];
            service[fieldName] = value;
        }
        
        if (cookerID) {
            // Remove any other services with the same cookerID, except the one at 'index'
            services = services.filter((s, i) => i == index || s.cookerID !== cookerID);
        }

        this.updateFields({ services }, null, !loadPrice);
        this.isChanged = true;
    }

    deleteService(cookerID) {
        const { appt } = this.state;
        const services = [...appt.services];
        const index = services.findIndex(ast => ast.cookerID == cookerID);
        if (index != -1) {
            services.splice(index, 1);
        }
        this.updateFields({ services });
    }

    async showPriceCalcModal() {
        const { appt } = this.state;

        // Calculate services to pre-populate quotation
        const services = {};
        appt.services.forEach(s => {
            services[s.serviceTypeID] = (services[s.serviceTypeID] || 0) + 1;
        });

        // Show modal
        const result = await this.priceCalcModalRef.current.open({
            priceSchemeID: appt.property.priceSchemeID,
            services,
            canUsePrice: true
        });

        // Update the price scheme for this property
        if (result.priceSchemeID) {
            await API.call('property/update-price-scheme', {
                priceSchemeID: result.priceSchemeID,
                propertyID: appt.propertyID,
            });
            this.loadProperty(true);
        }

        // Load price back in
        await this.loadServicePrice(null);
        this.updateFields({ servicePrice: result.price });
        this.isChanged = true;
    }
    
    async printReceipt(isInline) {
        await this.save();
        await Print.url('/api/appt/get-receipt-pdf/' + this.state.appt.id, {
            isInline
        });
        this.setState({ isLoading: false });
    }

    async printInvoice(isInline) {
        await this.save();
        const { invoiceID } = this.state.appt;
        if (invoiceID) {
            await Print.url('/api/invoice/get-pdf/' + invoiceID, {
                isInline
            });
        } else {
            alert('No invoice available for this appointment');
        }
        this.setState({ isLoading: false });
    }

    async addCustomer() {
        const { id } = await this.editCustomerModalRef.current.open({
            cameFrom: 'appt'
        });
        this.updateFields({
            customerID: id
        });
        this.isChanged = true;
        this.loadServicePrice(null);
    }

    updateTotal(appt) {
        appt.price = (appt.servicePrice || 0) + (appt.productPrice || 0) + (appt.cancellationFee || 0);
    }

    async loadSuggestedAppts(delta) {
        if (this.state.isLoadingSuggestedAppts) {
            return;
        }
        let { appt, suggestedApptsStartDate } = this.state;
        if (suggestedApptsStartDate) {
            suggestedApptsStartDate = moment(suggestedApptsStartDate).add(delta || 0, 'days');
        } else {
            suggestedApptsStartDate = moment().startOf('day');
        }
        suggestedApptsStartDate = moment(suggestedApptsStartDate).format('YYYY-MM-DD');
        this.setState({
            suggestedApptsStartDate,
            isLoadingSuggestedAppts: true,
            suggestedAppts: null
        });
        const { suggestedAppts, suggestedApptSettings } = await API.call('auto-planner/list-suggested-appts', {
            dateFrom: suggestedApptsStartDate,
            newAppt: appt
        });
        this.setState({
            isLoadingSuggestedAppts: false,
            suggestedAppts,
            suggestedApptSettings
        });
    }

    selectSuggestedApptDate(date) {
        this.updateFields({ date });
        if (this.props.onSetDate) {
            this.props.onSetDate(date);
        }
        this.isChanged = true;
    }

    getScaleColour(value, min, max) {
        // e.g min 500 max 600, value 550
        // (550 - 500) / (600 - 500) = 50 / 100 = 0.5
        const delta = max - min;
        const normalised = (delta > 0 ? (value - min) / delta : 0);
        const hue = 90 - normalised * 90;
        const col = DataHelpers.hslToHex(hue, 75, 90);
        return col;
    }

    selectSuggestedAppt(appt) {
        this.updateFields({
            date: appt.date,
            time: appt.time,
            technicianUserID: appt.technician.id,
            autoPlannerResultID: appt.autoPlannerResultID,
            autoPlannerResultOptionID: appt.autoPlannerResultOptionID
        });
        this.isChanged = true;
    }

    async confirmReopen() {
        const confirm = window.confirm('Are you sure you want to re-open this appointment?');
        if (confirm) {
            this.setState({ isLoading: true });
            await API.call('appt/reopen/' + this.state.appt.id);
            this.props.onClose(true);
        }
    }

    showCustomerComms() {
        const { appt } = this.state;
        
        this.customerCommsModalRef.current.open({
            customerID: appt.customerID,
            propertyID: appt.propertyID
        });
    }

    async confirmSendApptBookedNotification(apptID) {
        const send = await this.previewEmailModalRef.current.open({
            canSend: true,
            email: {
                type: 'ApptBooked',
                apptID
            }
        });
        if (send) {
            await API.call('appt/trigger-notification', {
                apptID,
                type: 'ApptBooked'
            });
        }
        return send;
    }

    async confirmResendCompletedNotification() {
        const { appt } = this.state;
        const send = await this.previewEmailModalRef.current.open({
            canSend: true,
            email: {
                type: 'ApptCompleted',
                apptID: appt.id
            }
        });
        if (send) {
            await API.call('appt/trigger-notification', {
                apptID: this.state.appt.id,
                type: 'ApptCompleted'
            });
            alert('The notification has been sent.');
        }
    }

    async editCustomer(customerID, propertyID) {
        await this.editCustomerModalRef.current.open({
            id: customerID,
            propertyID,
            cameFrom: 'appt'
        });
        this.loadCustomer();
        this.loadProperties();
        if (propertyID) {
            this.loadProperty(true);
            this.loadServicePrice(null);
        }
    }

    async showEditResultsModal() {
        const { productPrice } = await this.editApptResultsModalRef.current.open({
            id: this.state.appt.id
        });
        this.updateFields({
            productPrice
        });
    }

    getDiscountName(discount) {
        const dateParts = [];
        if (discount.startDate) {
            if (discount.expiryDate) {
                dateParts.push(moment(discount.startDate).format('DD/MM/YYYY') + ' - ' + moment(discount.expiryDate).format('DD/MM/YYYY'));
            } else {
                dateParts.push('from ' + moment(discount.startDate).format('DD/MM/YYYY'));
            }
        }
        else if (discount.expiryDate) {
            dateParts.push('until ' + moment(discount.expiryDate).format('DD/MM/YYYY'));
        }
        return discount.name + (dateParts.length > 0 ?
            ' (' + dateParts.join(', ') + ')' :
            ''
        );
    }

    async showEditInvoiceModal() {
        await this.editInvoiceModalRef.current.open({
            id: this.state.appt.invoice?.id
        });
    }

    //---------------------------------------------------------------------------------------------------------------

    render() {

        setTimeout(() => {
            this.focusInput = null;
        });
        
        return (<>

            {this.renderInner()}

            <PriceCalcModal ref={this.priceCalcModalRef} />
            <PreviewEmailModal ref={this.previewEmailModalRef} />
            <CustomerCommsModal ref={this.customerCommsModalRef} {...this.props} />
            <EditCustomerModal ref={this.editCustomerModalRef} {...this.props} />
            <EditApptResultsModal ref={this.editApptResultsModalRef} />
            <EditInvoiceModal ref={this.editInvoiceModalRef} {...this.props} />
        </>);
    }

    renderInner() {
        const {
            isLoading,
            isLoadingProperties,
            appt,
            nonBookingApptTypes,
            openApptID,
            openApptDate
        } = this.state;
        const { showWarningOverXMins } = this.props.loginInfo.account;
        const { hasControlPanel, warnTravelTime } = this.props;
        
        if (isLoading) {
            return (<Loader />);
        }
        const isCancelled = (appt.status == 'Cancelled' || appt.status == 'CustomerNotIn');

        return (
            <form onSubmit={e => { e.preventDefault(); e.stopPropagation(); this.saveAndClose() }} className="edit-appt-form">

                <fieldset>

                    <div className="row">

                        <div className="col-md-6">

                            {!appt.nonBookingApptTypeID &&
                                <div className="form-group">
                                    <label className="form-label">Customer</label>
                                    {this.renderCustomer()}
                                </div>
                            }

                            {!appt.customer &&
                                <div className="form-group">
                                    <label className="form-label">Non-Booking Time</label>
                                    <select
                                        value={appt.nonBookingApptTypeID || ''}
                                        className="form-control"
                                        onChange={e => {
                                            this.updateFields({ nonBookingApptTypeID: e.target.value });
                                            this.isChanged = true;
                                        }}
                                    >
                                        <option value="">(Select)</option>
                                        {nonBookingApptTypes.map(nbat =>
                                            <option key={nbat.id} value={nbat.id}>
                                                {nbat.name}
                                            </option>
                                        )}
                                    </select>
                                </div>
                            }

                        </div>

                        {appt.customer &&
                            <div className="col-md-2">

                                <div className="form-group">
                                    <label className="form-label">Activity</label>
                                    <button type="button" className="btn btn-secondary" onClick={() => this.showCustomerComms()}>
                                        View
                                    </button>
                                </div>

                            </div>
                        }

                        <div className={appt.customer ? 'col-md-4' : 'col-md-6'}>

                            {this.form.render('technicianUserID')}

                        </div>

                    </div>

                    {appt.customer && !!appt.customer.doNotCleanReason &&
                        <div className="warning">
                            <span className="fa-solid fa-triangle-exclamation" />{' '}
                            DO NOT CLEAN: {appt.customer.doNotCleanReason}
                        </div>
                    }

                    {isLoadingProperties ?
                        <div className="form-group">
                            <label className="form-label">Property</label>
                            <div className="form-control-plaintext"><Loader isInline /></div>
                        </div> :
                        !!appt.customerID ? this.form.render('propertyID') :
                            null
                    }

                    <div className="row">

                        <div className="col-md-3">

                            {this.form.render('date')}

                        </div>

                        <div className="col-md-3">

                            {this.form.render('time')}

                        </div>

                        <div className="col-md-3">

                            {this.form.render('duration')}

                        </div>

                        <div className="col-md-3">

                            {!!appt.id && !appt.nonBookingApptTypeID &&
                                <div className="form-group">
                                    <label className="form-label">Status</label>
                                    <p className="form-control-plaintext">
                                        {TextHelpers.getApptStatusFriendly(appt.status)}
                                        {(appt.status == 'Completed' || appt.status == 'Cancelled' || appt.status == 'CustomerNotIn') &&
                                            <button className="btn btn-primary btn-sm ms-2" title="Re-open appointment" onClick={() => this.confirmReopen()}>
                                                <span className="fa-solid fa-undo" />
                                            </button>
                                        }
                                    </p>
                                </div>
                            }

                        </div>

                    </div>

                    {warnTravelTime && appt.property && <>

                        <button type="button" className="btn btn-danger w-100 mb-3" onClick={() => {
                            alert('This appointment would go over the technician\'s limit of ' + showWarningOverXMins + ' mins travel time')
                        }}>
                            <span className="fa fa-triangle-exclamation"></span>{' '}
                            Travel time over {showWarningOverXMins}min limit
                        </button>

                    </>}

                    {!!openApptID && appt.property && <>

                        <button type="button" className="btn btn-danger w-100 mb-3" onClick={() => {
                            this.props.history.push(`calendar?apptID=${openApptID}&date=${openApptDate}`);
                            window.location.reload();
                        }}>
                            <span className="fa fa-triangle-exclamation"></span>{' '}
                            There is already an open booking at this property (click to open)
                        </button>

                    </>}

                    {appt.property && <>

                        {this.renderServices()}

                        {this.renderSuggestedAppts()}

                        {this.form.render('notes')}

                        {this.form.render('discountID')}

                        {(!isCancelled || appt.status == 'Completed') &&
                            <div className="row">

                                {!isCancelled &&
                                    <div className="col-md-4">

                                        {this.form.render('servicePrice')}

                                    </div>
                                }

                                {appt.status == 'Completed' && <>

                                    <div className="col-md-4">

                                        {this.form.render('productPrice')}

                                    </div>

                                    <div className="col-md-4">

                                        {this.form.render('price')}

                                    </div>
                                </>}

                            </div>
                        }
                        
                        {isCancelled &&
                            <div className="row">

                                <div className="col-md-4">

                                    {this.form.render('cancellationFee')}

                                </div>

                            </div>
                        }

                        {this.renderNotifications()}

                        {this.renderInvoice()}

                        {this.renderOther()}

                    </>}

                    {!!appt.nonBookingApptTypeID && this.renderNonBookingAppt()}

                </fieldset>

                {hasControlPanel &&
                    <div className="control-panel">

                        {this.renderControlPanel()}

                    </div>
                }

            </form>
        );
    }

    renderControlPanel() {
        const {
            appt
        } = this.state;
        const { onClose, onReschedule } = this.props;
        const hasSentBookingNotification = appt.notifications.some(n => n.type == 'ApptBooked');

        return (<>

            {onClose &&
                <button type="button" className="btn btn-secondary me-auto" onClick={() => onClose(false)}>
                    Cancel
                </button>
            }

            {appt.id && appt.status != 'Completed' &&
                <button
                    type="button"
                    className="btn btn-primary me-auto"
                    onClick={() => this.complete()}
                >
                    Complete Appointment
                </button>
            }

            {!!appt.id &&
                <div className="btn-group dropup">
                    <button type="button" className="btn btn-tertiary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        More...
                    </button>
                    <ul className="dropdown-menu">
                        {(appt.status == 'Completed' || !appt.customerID) && <>
                            <li>
                                <a className="dropdown-item" href="#" onClick={e => {
                                    e.preventDefault();
                                    this.showEditResultsModal();
                                }}>
                                    <span className="fa-solid fa-pencil-alt" />{' '}Edit results
                                </a>
                            </li>
                            <li className="dropdown-divider" />
                        </>}
                        {(appt.status == 'Open' || !appt.customerID) && <>
                            {onReschedule &&
                                <li>
                                    <a className="dropdown-item" href="#" onClick={e => {
                                        e.preventDefault();
                                        onReschedule();
                                    }}>
                                        <span className="fa-solid fa-calendar-days" />{' '}Reschedule
                                    </a>
                                </li>
                            }
                            <li>
                                <a className="dropdown-item" href="#" onClick={e => {
                                    e.preventDefault();
                                    this.confirmCancel();
                                }}>
                                    <span className="fa-solid fa-xmark" />{' '}Cancel appointment
                                </a>
                            </li>
                            {appt.recurringAppt &&
                                <li>
                                    <a className="dropdown-item" href="#" onClick={e => {
                                        e.preventDefault();
                                        this.confirmCancelRecurring();
                                    }}>
                                        <span className="fa-solid fa-xmark" />{' '}Cancel recurring appointment
                                    </a>
                                </li>
                            }
                        </>}
                        {appt.status == 'Open' && !!appt.customerID &&
                            <li>
                                <a className="dropdown-item" href="#" onClick={e => {
                                    e.preventDefault();
                                    this.confirmSendApptBookedNotification(appt.id);
                                }}>
                                    <span className="fa-solid fa-envelope" />{' '}Send booking email...
                                </a>
                            </li>
                        }
                        {appt.invoice && <>
                            <li className="dropdown-divider" />
                            <li>
                                <a className="dropdown-item" href="#" onClick={e => {
                                    e.preventDefault();
                                    this.printInvoice(true);
                                }}>
                                    <span className="fa-solid fa-file-invoice" />{' '}Print invoice
                                </a>
                            </li>
                            <li>
                                <a className="dropdown-item" href="#" onClick={e => {
                                    e.preventDefault();
                                    this.printInvoice(false);
                                }}>
                                    <span className="fa-solid fa-file-invoice" />{' '}Download invoice
                                </a>
                            </li>
                            <li>
                                <a className="dropdown-item" href="#" onClick={e => {
                                    e.preventDefault();
                                    this.emailInvoice();
                                }}>
                                    <span className="fa-solid fa-envelope" />{' '}Email invoice...
                                </a>
                            </li>
                        </>}
                        <li className="dropdown-divider" />
                        <li>
                            <a className="dropdown-item" href="#" onClick={e => {
                                e.preventDefault();
                                this.printReceipt(true);
                            }}>
                                <span className="fa-solid fa-receipt" />{' '}Print receipt
                            </a>
                        </li>
                        <li>
                            <a className="dropdown-item" href="#" onClick={e => {
                                e.preventDefault();
                                this.printReceipt(false);
                            }}>
                                <span className="fa-solid fa-receipt" />{' '}Download receipt
                            </a>
                        </li>
                        <li>
                            <a className="dropdown-item" href="#" onClick={e => {
                                e.preventDefault();
                                this.emailReceipt();
                            }}>
                                <span className="fa-solid fa-envelope" />{' '}Email receipt...
                            </a>
                        </li>
                    </ul>
                </div>
            }

            <button type="button" className="btn btn-primary ms-auto" onClick={() => this.saveAndClose()}>
                Save & Close
            </button>

        </>);
    }

    renderCustomer() {
        const { isLoadingCustomer, appt, isLocked } = this.state;

        if (isLoadingCustomer) {
            return (
                <div className="form-control-plaintext">
                    <Loader isInline />
                </div>
            );
        }
        if (appt.customer) {
            return (<>
                <div className="form-control-plaintext">
                    {TextHelpers.formatName(appt.customer.title, appt.customer.firstName, appt.customer.lastName) || appt.customer.companyName}
                    <button type="button" className="btn btn-sm btn-primary ms-2" title="View customer" onClick={() => this.editCustomer(appt.customer.id, appt.property ? appt.property.id : null)}
                    >
                        <span className="fa fa-search" />
                    </button>
                    {!isLocked &&
                        <button type="button" className="btn btn-sm btn-danger ms-2" title="Select a different customer" onClick={() => {
                            this.updateFields({ customerID: null });
                            this.isChanged = true;
                        }}>
                            <span className="fa fa-times" />
                        </button>
                    }
                </div>

                <div className="appt-editor-contact-details">
                    Email: {appt.customer.emailAddress || '(None)'}<br />
                    Tel: {appt.customer.mobileTel || appt.customer.landlineTel || appt.customer.secondaryTel || '(None)'}
                </div>

                {!!appt.customer.notes &&
                    <div className="appt-editor-important-notes">
                        <span className="fa-solid fa-comment" />{' '}
                        {appt.customer.notes}
                    </div>
                }
            </>);
        }

        if (isLocked) {
            return null;
        }

        setTimeout(() => {
            this.isRendered = true;
        }, 0);

        return (
            <SearchBox
                className="form-control"
                placeholder="Search..."
                minLength={2}
                autoFocus={!this.isRendered}
                search={async (query, setResults, dataObj) => {
                    dataObj.nonce = `${Math.random()}`;
                    const response = await API.call('search/customer-property', { query, isArchived: false, nonce: dataObj.nonce });
                    if (response.nonce == dataObj.nonce) {
                        const customersFlattened = [];
                        response.results.forEach(c => {
                            if (c.hasMultipleProperties) {
                                customersFlattened.push({
                                    ...c
                                });
                            }
                            c.properties.forEach(p => {
                                const customerAndProperty = {
                                    ...c,
                                    isProperty: true,
                                    property: p
                                };
                                customersFlattened.push(customerAndProperty);
                            });
                        });
                        setResults(customersFlattened);
                    }
                }}
                renderInputExtras={(query, results) =>
                    <button type="button" className="btn btn-tertiary" onClick={() => this.addCustomer()}>
                        <span className="fa-solid fa-plus" />
                    </button>
                }
                //renderExtras={(query, results) => 
                //    <div className="search-results-button">
                //        <button type="button" className="btn btn-tertiary" onClick={() => this.addCustomer()}>
                //            <span className="fa-solid fa-plus" />{' '}
                //            New Customer
                //        </button>
                //    </div>
                //}
                onClickResult={customer => {
                    this.updateFields({
                        customerID: customer.id,
                        propertyID: (customer.property ? customer.property.id : null)
                    });
                    this.isChanged = true;
                }}
            />
        );
    }

    renderServices() {
        const { appt, isLocked } = this.state;

        if (!appt.property || !appt.property.cookers) {
            return (<Loader />);
        }
        
        return (<>

            {this.renderCookers()}

            <div className="form-group">
                {this.renderOtherServicesTable()}
            </div>

        </>);
    }

    renderCookers() {
        const { appt, isLoadingProperty, isLocked, serviceTypes } = this.state;

        if (isLoadingProperty) {
            return (<Loader />);
        } else if (!appt.property || !appt.property.cookers) {
            return null;
        }

        // Highlight any cookers that are present in the appointment but not actually in the property
        let numMissingCookers = 0;
        appt.services.forEach(ast => {
            if (ast.cookerID && !appt.property.cookers.find(c => c.id == ast.cookerID)) {
                numMissingCookers++;
            }
        });

        return (<>
            <table className="appt-cookers-table table table-bordered">
                <thead>
                    <tr>
                        <th className="cooker-col">Cooker</th>
                        <th className="service-col">Service</th>
                    </tr>
                </thead>
                <tbody>
                    {appt.property.cookers.map(cooker => {
                        const service = appt.services.find(ast => ast.cookerID == cooker.id) || { cookerID: cooker.id };

                        return (
                            <tr key={cooker.id}>
                                <td className="cooker-col">
                                    {cooker.cookerType.name || 'Unspecified'}
                                    {(!!cooker.floor || !!cooker.location) && <>
                                        <br />
                                        {!!cooker.floor && <>
                                            {cooker.floor}{' '}floor{' '}
                                        </>}
                                        {cooker.location}
                                    </>}
                                    {!!cooker.notes &&
                                        <div className="appt-editor-important-notes">
                                            <span className="fa-solid fa-comment" />{' '}
                                            {cooker.notes}
                                        </div>
                                    }
                                    {!cooker.isInUse && <><br />(Not in use)</>}
                                </td>
                                <td className="service-col">

                                    <select
                                        className="form-control"
                                        disabled={isLocked}
                                        value={service.serviceTypeID}
                                        onChange={e => {
                                            const serviceTypeID = Number(e.target.value);
                                            if (serviceTypeID) {
                                                this.updateService(cooker.id, null, { serviceTypeID }, true);
                                            } else {
                                                this.deleteService(cooker.id);
                                            }
                                        }}
                                    >
                                        <option value="">(Select)</option>
                                        {serviceTypes.map(st =>
                                            <option key={st.id} value={st.id}>
                                                {st.name}
                                            </option>
                                        )}
                                    </select>

                                    {!!service.serviceTypeID &&
                                        <CurrencyInput
                                            className="form-control"
                                            value={service.price === 0 ? '0' : service.price}
                                            placeholder="Price..."
                                            onValueChange={(values) => {
                                                this.updateService(cooker.id, null, {
                                                    price: Number(values.value) || ''
                                                }, false);
                                            }}
                                        />
                                    }
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {numMissingCookers > 0 &&
                <div className="alert alert-info mb-3">
                    <b>Note</b>: This appointment includes {numMissingCookers}
                    {numMissingCookers == 1 ? ' cooker that has ' : ' cookers that have '} 
                    been deleted or moved from this property.
                </div>
            }

        </>);
    }

    renderOtherServicesTable() {
        const { appt, serviceTypes, serviceTypesByID, isLocked } = this.state;
        const otherServices = [];
        appt.services.forEach((ast, index) => {
            if (!ast.cookerID) {
                ast.index = index;
                otherServices.push(ast);
            }
        });
        // TODO lock if isLocked=true

        if (serviceTypes.filter(st => !st.requiresCooker).length == 0) {
            return null;
        }

        return (
            <table className="table table-bordered other-services-table">
                <thead>
                    <tr>
                        <th className="service-col">Other Services</th>
                        <th className="price-col">Price</th>
                        <th className="actions-col"></th>
                    </tr>
                </thead>
                <tbody>
                    {otherServices.map(otherService => {
                        const serviceType = serviceTypesByID[otherService.serviceTypeID] || { name: 'UNKNOWN' };
                        return (
                            <tr key={otherService.serviceTypeID}>
                                <td className="service-col">{serviceType.name}</td>
                                <td className="price-col">
                                    <CurrencyInput
                                        className="form-control"
                                        value={otherService.price || ''}
                                        placeholder="Price..."
                                        onValueChange={(values) => {
                                            this.updateService(null, otherService.index, { price: Number(values.value) || '' });
                                        }}
                                    />
                                </td>
                                <td className="actions-col">

                                    <button type="button" className="btn btn-danger btn-sm" onClick={() => this.toggleService(serviceType.id)}>
                                        <span className="fa fa-times" />
                                    </button>

                                </td>
                            </tr>
                        );
                    })}
                    {!isLocked &&
                        <tr>
                            <td colSpan={3}>

                                {this.renderOtherServicesSelector()}

                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        );
    }

    renderOtherServicesSelector() {
        const { serviceTypes, appt } = this.state;
        const otherServiceTypes = serviceTypes.filter(st => !st.requiresCooker);
        const selectedServiceTypes = {};
        appt.services.forEach(ast => {
            if (!ast.cookerID) {
                selectedServiceTypes[ast.serviceTypeID] = true
            }
        });

        return (
            <div className="btn-group other-services-selector">
                <button type="button" className="btn btn-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    Add a service...
                </button>
                <ul className="dropdown-menu">
                    {otherServiceTypes.map(st => {
                        let price = null;
                        if (appt.property && appt.property.priceSchemeID) {
                            price = st.priceSchemes.find(ps => ps.priceSchemeID == appt.property.priceSchemeID)?.firstPrice;
                        }

                        return (
                            <li key={st.id}>
                                <a className={`dropdown-item ${selectedServiceTypes[st.id] ? 'active' : ''}`} href="#" onClick={e => {
                                    e.preventDefault();
                                    this.toggleService(st.code, null);
                                }}>
                                    {selectedServiceTypes[st.id] ?
                                        <span className="fa-solid fa-check" /> :
                                        <span className="fa-solid fa-xmark" />
                                    }
                                    <span className="other-service-name">
                                        {st.name}
                                    </span>
                                    {!!price && price > 0 &&
                                        <span className="other-service-price">
                                            {TextHelpers.formatCurrency(price)}
                                        </span>
                                    }
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }

    renderSuggestedAppts() {
        const { appt, suggestedAppts, suggestedApptsStartDate } = this.state;
        if (!appt.property || !appt.duration || appt.status != 'Open') {
            return null;
        }
        let canGoBack = false;
        if (suggestedAppts) {
            canGoBack = !moment().startOf('day').isSameOrAfter(moment(suggestedApptsStartDate));
        }

        return (
            <div className="form-group suggested-appts">
                <label className="form-label">
                    Suggested dates / times
                    ({moment(suggestedApptsStartDate).format('DD/MM/YYYY')} - {moment(suggestedApptsStartDate).add(4 * 7, 'days').format('DD/MM/YYYY')})

                    {suggestedAppts &&
                        <div className="ms-auto">
                            {canGoBack &&
                                <button type="button" className="btn btn-tertiary me-1" onClick={() => this.loadSuggestedAppts(-7 * 4)}>
                                    <span className="fa-solid fa-chevron-left" />{' '}
                                    -1 week
                                </button>
                            }

                            <button type="button" className="btn btn-tertiary" onClick={() => this.loadSuggestedAppts(+7 * 4)}>
                                +1 week{' '}
                                <span className="fa-solid fa-chevron-right" />
                            </button>
                        </div>
                    }

                </label>
                {this.renderSuggestedApptsInner()}
            </div>
        );
    }

    renderSuggestedApptsInner() {
        const { appt, suggestedAppts, suggestedApptSettings, isLoadingSuggestedAppts } = this.state;
        const { routingProfileName } = this.props.loginInfo.account;

        if (!suggestedAppts) {
            return (
                <div>
                    {isLoadingSuggestedAppts ?
                        <Loader /> :
                        <button type="button" className="btn btn-primary" onClick={() => this.loadSuggestedAppts(0)}>
                            <span className="fa-solid fa-lightbulb" />{' '}
                            Get suggestions (Profile: {routingProfileName || 'None'})
                        </button>
                    }
                </div>
            );
        }

        // Put appts into correct bands
        const suggestedApptBands = {
            a: { name: 'Great', suggestedAppts: [] },
            b: { name: 'Good', suggestedAppts: [] },
            c: { name: 'OK', suggestedAppts: [] }
        };
        suggestedAppts.forEach(sa => {
            let band;
            if (sa.cost <= suggestedApptSettings.maxCostBandA) band = 'a';
            else if (sa.cost < suggestedApptSettings.maxCostBandB) band = 'b';
            else band = 'c';
            const suggestedApptBand = suggestedApptBands[band];
            suggestedApptBand.suggestedAppts.push(sa);
        });

        // Sort within the band
        Object.keys(suggestedApptBands).forEach(sab => {
            const suggestedApptBand = suggestedApptBands[sab];
            suggestedApptBand.suggestedAppts.sort((a, b) => a.cost - b.cost);
        });
        
        return (
            <div className="bands">
                {Object.keys(suggestedApptBands).map(sab => {
                    const suggestedApptBand = suggestedApptBands[sab];
                    return (
                        <div className="band" key={sab}>
                            <div className="band-name">
                                {suggestedApptBand.name}
                            </div>
                            {suggestedApptBand.suggestedAppts.length > 0 ?
                                <ul className="appts">
                                    {suggestedApptBand.suggestedAppts.map((suggestedAppt, index) =>
                                        <li
                                            key={index}
                                            className={suggestedAppt.date == appt.date && suggestedAppt.time == appt.time && suggestedAppt.technician.id == appt.technicianUserID ? 'active' : ''}
                                            style={{
                                                backgroundColor: this.getScaleColour(suggestedAppt.cost, 0, suggestedApptSettings.maxCostBandC),
                                                borderColor: suggestedAppt.techician.diaryBackColour
                                            }}
                                            onClick={() => this.selectSuggestedAppt(suggestedAppt)}
                                            title={suggestedAppt.notes.join('\r\n')}
                                        >
                                            <div className="appt-inner">
                                                {moment(suggestedAppt.date).format('ddd Do MMM')} <br />
                                                {suggestedAppt.time} with {suggestedAppt.technician.nickname}
                                            </div>
                                        </li>
                                    )}
                                </ul> :
                                <div className="empty-text">Sorry, couldn't find any {suggestedApptBand.name} appointments</div>
                            }
                        </div>
                    );
                })}      
            </div>
        );
    }

    renderNotifications() {
        const { appt } = this.state;
        const hasSentBookingNotification = appt.notifications.some(n => n.type == 'ApptBooked');

        return (
            <div className="form-group">
                <label className="form-label">
                    Notifications
                </label>
                {appt.notifications.length > 0 ?
                    <ul>
                        {appt.notifications.map((n, index) =>
                            <li key={index}>
                                {ReferenceHelpers.formatNotification(n)}
                            </li>
                        )}
                    </ul> :
                    <p className="form-control-plaintext">No notifications sent yet</p>
                }
                {!hasSentBookingNotification && 
                    this.form.render('skipBookingNotification')    
                }
            </div>
        );
    }

    renderInvoice() {
        const { appt } = this.state;
        if (!appt.invoice || !appt.invoice.number) return null;

        return (
            <div className="form-group">
                <label className="form-label">
                    Invoice

                    {appt.invoice && appt.invoice.qbid &&
                        <a className="btn btn-primary ms-auto" href={`https://app.qbo.intuit.com/app/invoice?txnId=${appt.invoice.qbid}`} target="_blank" rel="noreferrer">
                            <span className="fa-solid fa-eye" />{' '}
                            View in QB
                        </a>
                    }
                </label>
                {appt.invoice && !!appt.invoice.number &&
                    <p className="form-control-plaintext">
                        Invoice number: <strong>{appt.invoice.number}</strong>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary ms-2"
                            title="View invoice"
                            onClick={() => this.showEditInvoiceModal(appt.invoice.id)}
                        >
                            View
                        </button>
                    </p>
                }
            </div>
        );
    }

    renderOther() {
        const { appt } = this.state;
        if (!appt.bookedByName && !appt.dateTimeBooked) {
            return null;
        }
        return (
            <div className="form-group">
                <label className="form-label">Other info</label>
                <p className="form-control-plaintext">
                    Booked
                    {appt.isBookedOnline && <> online</>}
                    {!!appt.bookedByName && <> by {appt.bookedByName}</>}
                    {(!!appt.bookedByName || appt.isBookedOnline) && <> on {moment(appt.dateTimeBooked).format('DD/MM/YYYY')}</>}
                </p>
            </div>
        );
    }

    renderNonBookingAppt() {
        const { appt } = this.state;

        return (<>

            {this.form.render('notes')}

            {(!appt.id || appt.isRecurring) && this.form.render('isRecurring')}

            {appt.isRecurring && <>

                {this.recurringApptForm.render([
                    'startDate',
                    'endDate'
                ])}

                <div className="row">

                    <div className="col-md-6">

                        {this.recurringApptForm.render('frequencyType')}

                    </div>

                    <div className="col-md-6">

                        {this.recurringApptForm.render('frequency')}

                    </div>

                </div>
            </>}

        </>);
    }
}

export default EditAppt;